/**
 * Bloco institucional da eMaster.
 *
 * Fixo em toda proposta: aumenta a percepção de autoridade e de valor percebido antes de o cliente
 * chegar aos preços. A ordem foi pensada para construir confiança: quem somos → competências
 * validadas pela AWS → volume sob gestão → time dedicado.
 *
 * As competências variam por trilha: Government em PS, SMB em CS, Migration e SPP em ambas.
 */

import type { Trilha } from '@emaster/dominio';

import { escaparHtml } from './escapar.ts';
import { CONTAINER, FALLBACK_IMAGEM, icone } from './tokens.ts';

// ---------------------------------------------------------------------------
// Assets de competências
// ---------------------------------------------------------------------------

const SELOS = {
  spp: 'https://committed.proposta.emaster.info/images/partner-advanced-services-badge.png',
  migration: 'https://committed.proposta.emaster.info/images/migration-competency-badge.png',
  government: 'https://committed.proposta.emaster.info/images/Gov-Competency-Badge_small.png',
  smb: 'https://committed.proposta.emaster.info/images/SMB-partner.png',
} as const;

interface Competencia {
  readonly nome: string;
  readonly imagem: string;
  readonly descricao: string;
}

function competenciasPorTrilha(trilha: Trilha): readonly Competencia[] {
  const base: Competencia[] = [
    {
      nome: 'AWS Advanced Tier Services Partner',
      imagem: SELOS.spp,
      descricao:
        'Parceria validada pela AWS desde 2017, com revisão anual de capacidade técnica e satisfação de cliente.',
    },
    {
      nome: 'Migration Competency',
      imagem: SELOS.migration,
      descricao:
        'Competência validada em projetos de migração para a nuvem AWS, com metodologia e ferramental comprovados.',
    },
  ];

  if (trilha === 'PS') {
    base.push({
      nome: 'Government Competency',
      imagem: SELOS.government,
      descricao:
        'Competência validada pela AWS para atuação no setor público, com conformidade regulatória e experiência em licitações.',
    });
  }

  if (trilha === 'CS') {
    base.push({
      nome: 'SMB Competency',
      imagem: SELOS.smb,
      descricao:
        'Competência validada para atendimento a pequenas e médias empresas, com modelo de operação adequado ao porte.',
    });
  }

  return base;
}

// ---------------------------------------------------------------------------
// Diferenciais
// ---------------------------------------------------------------------------

interface Diferencial {
  readonly iconeNome: string;
  readonly titulo: string;
  readonly descricao: string;
}

const DIFERENCIAIS: readonly Diferencial[] = [
  {
    iconeNome: 'badge-check',
    titulo: 'Parceira AWS desde 2017',
    descricao:
      'AWS Advanced Tier Services Partner com competências validadas. Revisão anual de capacidade técnica e satisfação de cliente.',
  },
  {
    iconeNome: 'layers',
    titulo: '+1.000 projetos entregues',
    descricao:
      'Mais de R$ 20 milhões em consumo sob gestão, com atuação recorrente em contas críticas institucionais e corporativas.',
  },
  {
    iconeNome: 'hand-coins',
    titulo: 'FinOps e engenharia de valor',
    descricao:
      'Análises de TCO, Savings Plans e otimização contínua de custos — determinante para manter o investimento previsível.',
  },
  {
    iconeNome: 'headset',
    titulo: 'Suporte dedicado em português',
    descricao:
      'Atendimento direto com arquitetos certificados, sem triagem por chatbot. Chamados ilimitados no plano SafePlace.',
  },
];

// ---------------------------------------------------------------------------
// Renderização
// ---------------------------------------------------------------------------

/**
 * Bloco institucional. Sempre presente, não depende de preenchimento pelo vendedor.
 *
 * Posição: logo após o herói, antes de qualquer seção de texto ou de investimento. O objetivo é
 * que o cliente conheça quem está falando antes de ler o que está sendo proposto.
 */
export function blocoInstitucional(trilha: Trilha): string {
  const competencias = competenciasPorTrilha(trilha);

  const cardsCompetencia = competencias
    .map(
      (c) => `<div class="flex flex-col items-center text-center">
                    <img src="${escaparHtml(c.imagem)}" alt="${escaparHtml(c.nome)}" class="h-20 w-32 object-contain mb-3 mx-auto" ${FALLBACK_IMAGEM}>
                    <h4 class="font-bold text-brand-blue text-sm mb-1">${escaparHtml(c.nome)}</h4>
                    <p class="text-xs text-gray-500 leading-relaxed">${escaparHtml(c.descricao)}</p>
                </div>`,
    )
    .join('\n                ');

  const cardsDiferencial = DIFERENCIAIS.map(
    (
      d,
    ) => `<div class="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div class="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center text-brand-accent mb-4">
                        ${icone(d.iconeNome, 'w-6 h-6')}
                    </div>
                    <h3 class="font-bold text-brand-blue mb-2">${escaparHtml(d.titulo)}</h3>
                    <p class="text-sm text-gray-500">${escaparHtml(d.descricao)}</p>
                </div>`,
  ).join('\n                ');

  return `
    <section class="py-16 bg-white border-b border-gray-100">
        <div class="${CONTAINER}">
            <div class="mb-10 max-w-3xl">
                <span class="text-sm font-semibold text-brand-accent uppercase tracking-wider">Por que a eMaster</span>
                <h2 class="text-3xl font-bold text-brand-blue mt-2 mb-4">O que só a eMaster entrega</h2>
                <div class="w-20 h-1.5 bg-brand-accent rounded-full"></div>
                <p class="text-gray-600 text-lg leading-relaxed mt-6">
                    A eMaster não é um braço meramente operacional ou financeiro da AWS. Somos o elo que conecta a sua equipe ao ecossistema completo da Amazon Web Services — incluindo os times especializados, os programas de crédito e as ferramentas de otimização que só parceiros avançados acessam.
                </p>
            </div>

            <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                ${cardsDiferencial}
            </div>

            <div class="bg-gray-50 rounded-3xl p-8 border border-gray-100">
                <h3 class="text-center font-bold text-brand-blue mb-8">Competências validadas pela AWS</h3>
                <div class="grid grid-cols-2 sm:grid-cols-${String(competencias.length)} gap-8 items-start">
                    ${cardsCompetencia}
                </div>
            </div>
        </div>
    </section>`;
}
